import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2 } from "lucide-react";
import { useFeature } from "@/lib/auth";

type Queue = { id: string; name: string; routingStrategy?: string; waiting?: number };
type Ivr = { id: string; name: string; greeting?: string };

export default function AdminCallSettings() {
  const hasIvr = useFeature("calls.ivr");

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-4" data-testid="page-admin-call-settings">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Call Settings</h1>
        <p className="text-sm text-muted-foreground">Manage queues and IVR menus.</p>
      </div>
      <Tabs defaultValue="queues">
        <TabsList>
          <TabsTrigger value="queues" data-testid="tab-queues">Queues</TabsTrigger>
          {hasIvr && <TabsTrigger value="ivr" data-testid="tab-ivr">IVR</TabsTrigger>}
        </TabsList>
        <TabsContent value="queues"><QueuesTab /></TabsContent>
        {hasIvr && <TabsContent value="ivr"><IvrTab /></TabsContent>}
      </Tabs>
    </div>
  );
}

function QueuesTab() {
  const [name, setName] = useState("");
  const queues = useQuery<Queue[]>({ queryKey: ["/api/call-queues"] });
  const create = useMutation({
    mutationFn: async () => apiRequest("POST", "/api/call-queues", { name, routingStrategy: "round_robin" }),
    onSuccess: () => { setName(""); queryClient.invalidateQueries({ queryKey: ["/api/call-queues"] }); },
  });
  const remove = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/call-queues/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/call-queues"] }),
  });
  return (
    <Card>
      <CardHeader><CardTitle className="text-sm">Queues</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="Queue name" aria-label="Queue name" data-testid="input-queue-name" />
          <Button onClick={() => create.mutate()} disabled={!name.trim()} data-testid="button-create-queue">
            <Plus className="w-3 h-3 mr-1" />Add
          </Button>
        </div>
        <div className="space-y-1">
          {(queues.data || []).map(q => (
            <div key={q.id} className="flex items-center justify-between border rounded-md p-2" data-testid={`row-queue-${q.id}`}>
              <div>
                <div className="text-sm font-medium">{q.name}</div>
                <div className="text-xs text-muted-foreground">{q.routingStrategy || "round_robin"}</div>
              </div>
              <IconButton variant="ghost" label={`Deactivate queue ${q.name}`} onClick={() => remove.mutate(q.id)} data-testid={`button-delete-queue-${q.id}`}>
                <Trash2 className="w-3 h-3" aria-hidden="true" />
              </IconButton>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function IvrTab() {
  const [name, setName] = useState("");
  const [greeting, setGreeting] = useState("");
  const menus = useQuery<Ivr[]>({ queryKey: ["/api/ivr-menus"] });
  const create = useMutation({
    mutationFn: async () => apiRequest("POST", "/api/ivr-menus", { name, greeting }),
    onSuccess: () => { setName(""); setGreeting(""); queryClient.invalidateQueries({ queryKey: ["/api/ivr-menus"] }); },
  });
  const remove = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/ivr-menus/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/ivr-menus"] }),
  });
  return (
    <Card>
      <CardHeader><CardTitle className="text-sm">IVR Menus</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-2">
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="Menu name" aria-label="IVR menu name" data-testid="input-ivr-name" />
          <Input value={greeting} onChange={e => setGreeting(e.target.value)} placeholder="Greeting" aria-label="IVR greeting message" data-testid="input-ivr-greeting" />
          <Button onClick={() => create.mutate()} disabled={!name.trim()} data-testid="button-create-ivr">
            <Plus className="w-3 h-3 mr-1" />Add
          </Button>
        </div>
        <div className="space-y-1">
          {(menus.data || []).map(m => (
            <div key={m.id} className="flex items-center justify-between border rounded-md p-2" data-testid={`row-ivr-${m.id}`}>
              <div>
                <div className="text-sm font-medium">{m.name}</div>
                <div className="text-xs text-muted-foreground truncate">{m.greeting}</div>
              </div>
              <IconButton variant="ghost" label={`Deactivate IVR menu ${m.name}`} onClick={() => remove.mutate(m.id)} data-testid={`button-delete-ivr-${m.id}`}>
                <Trash2 className="w-3 h-3" aria-hidden="true" />
              </IconButton>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
